import React from 'react';

import { Button, Dialog, Heading } from '@betterment/samba-react';

type DialogProps = Partial<Omit<React.ComponentProps<typeof Dialog>, 'children'>>;
type DialogTriggerProps = Partial<Omit<React.ComponentProps<typeof Dialog.Trigger>, 'children'>>;
type DialogContentProps = Partial<Omit<React.ComponentProps<typeof Dialog.Content>, 'children'>>;
type DialogTitleProps = Partial<Omit<React.ComponentProps<typeof Dialog.Title>, 'children'>>;

type AccountSettingsModalProps = {
  children: React.ReactNode;
  trigger?: React.ReactNode;
  title?: React.ReactNode;
  headingVariant?: React.ComponentProps<typeof Heading>['variant'];
  dialogProps?: DialogProps;
  triggerProps?: DialogTriggerProps;
  contentProps?: DialogContentProps;
  titleProps?: DialogTitleProps;
};

const defaultTrigger = (
  <Button variant="ghostSecondary">
    <Button.Icon name="Gear" />
    Settings
  </Button>
);

export const AccountSettingsModal: React.FC<AccountSettingsModalProps> = ({
  children,
  trigger = defaultTrigger,
  title = 'Account settings',
  headingVariant = 'title300',
  dialogProps,
  triggerProps,
  contentProps,
  titleProps,
}) => {
  const triggerPropsWithDefaults: DialogTriggerProps = {
    ...(triggerProps ?? {}),
    asChild: triggerProps?.asChild ?? true,
  };

  const contentCssOverrides = contentProps?.css;
  const contentSize = contentProps?.size ?? 'large';
  const ariaDescribedBy = contentProps?.['aria-describedby'];
  const mergedContentCss = {
    display: 'grid',
    gap: 'space.100',
    p: 'space.400',
    overflowY: 'auto',
    ...contentCssOverrides,
  };

  const titlePropsWithDefaults: DialogTitleProps = {
    asChild: true,
    ...titleProps,
  };

  const renderTitle =
    typeof title === 'string' ? (
      <Heading as="h2" variant={headingVariant}>
        {title}
      </Heading>
    ) : (
      title
    );

  return (
    <Dialog {...dialogProps}>
      <Dialog.Trigger {...triggerPropsWithDefaults}>{trigger}</Dialog.Trigger>
      <Dialog.Content
        {...(contentProps ?? {})}
        size={contentSize}
        css={mergedContentCss}
        aria-describedby={ariaDescribedBy}
      >
        <Dialog.Title {...titlePropsWithDefaults}>{renderTitle}</Dialog.Title>
        {children}
      </Dialog.Content>
    </Dialog>
  );
};
